import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { type AiDownloadPreparation } from "../downloadCondition";
import { type AiStoredPictureSearchQuery } from "../types";
import { searchConditionLabel } from "../labels";

interface AiStoredPicturesDownloadDialogProps {
  open: boolean;
  preparation: AiDownloadPreparation;
  query: AiStoredPictureSearchQuery;
  isPriceHidden: boolean;
  isReadOnly: boolean;
  isSubmitting: boolean;
  errorMessage: string;
  onClose: () => void;
  onSubmit: () => void;
}

const SummaryRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "120px 1fr" },
      gap: { xs: 0.25, sm: 2 },
      py: 1.25,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {children}
    </Typography>
  </Box>
);

export const AiStoredPicturesDownloadDialog: React.FC<
  AiStoredPicturesDownloadDialogProps
> = ({
  open,
  preparation,
  query,
  isPriceHidden,
  isReadOnly,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
}) => (
  <Dialog
    open={open}
    onClose={isSubmitting ? undefined : onClose}
    fullWidth
    maxWidth="sm"
    PaperProps={{ sx: { borderRadius: 1 } }}
  >
    <DialogTitle sx={{ fontWeight: 700 }}>登記取得の確認</DialogTitle>
    <DialogContent>
      <SummaryRow label="対象件数">
        {preparation.billableCount + preparation.freeCount}件
      </SummaryRow>
      <Divider />
      <SummaryRow label="検索条件">{searchConditionLabel(query)}</SummaryRow>
      <Divider />
      <SummaryRow label="取得内訳">
        未取得・有料 {preparation.billableCount}件 / 取得済み・無料{" "}
        {preparation.freeCount}件
      </SummaryRow>
      <Divider />
      <SummaryRow label="概算費用">
        {isPriceHidden
          ? preparation.billableCount > 0
            ? "未取得の登記を含みます"
            : "0円"
          : `${preparation.estimatedChargeAmount.toLocaleString()}円`}
      </SummaryRow>
      {preparation.billableCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          未取得の登記は、取得時に料金と利用権限を再確認します
        </Alert>
      )}
      {errorMessage !== "" && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button disabled={isSubmitting} onClick={onClose}>
        戻る
      </Button>
      <ReadOnlyTooltip isReadOnly={isReadOnly}>
        <span>
          <Button
            variant="contained"
            color={preparation.billableCount > 0 ? "warning" : "primary"}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadDoneOutlinedIcon />
              )
            }
            disabled={isReadOnly || isSubmitting}
            onClick={onSubmit}
          >
            この内容で取得する
          </Button>
        </span>
      </ReadOnlyTooltip>
    </DialogActions>
  </Dialog>
);
